import React, { useEffect, useRef } from "react";
import * as d3 from "d3";

import { getBarFillWithEmptyThreshold } from "@tether-web-portal/occupancy/utilisationLevel";
import { asFraction01, asPercent0100, percentToFraction } from "@tether-web-portal/occupancy/units";

// Values below this (as a percent, 0..100) render with the EMPTY grey so the
// chart doesn't show a purple "LOW" bar for barely-used hours.
const BAR_EMPTY_PERCENT_THRESHOLD = asPercent0100(10);

export interface OccupancyBarData {
  id?: number;
  label: string;
  value: number;
  tooltip?: string;
}

interface OccupancyBarChartProps {
  data: OccupancyBarData[];
  selectedBar?: number;
  max?: number;
  width: number;
  height: number;
  enableXLabels?: boolean;
  /**
   * Show a label only on every Nth bar. 1 (default) shows every bar, 3 shows
   * 1-in-3, etc. Used when there are too many bars to label each one
   * (e.g. 24 hours, 31 days) — dropping to every 3rd/5th keeps the axis
   * readable without rotating the labels.
   */
  xLabelEvery?: number;
  /**
   * Degrees to rotate x-axis labels (0 = horizontal, -45 = tilted upward).
   * When non-zero, the bottom margin is grown so the rotated labels have
   * room without overlapping adjacent tiles.
   */
  xLabelRotation?: number;
  /** Transform a bar's `label` into the string shown on the x-axis. */
  xLabelFormatter?: (label: string, index: number) => string;
}

const SMALL_BAR_MAX_ITEM = 7;
const SMALL_BAR_WIDTH = 18; //18px

const OccupancyBarChart = ({
  data,
  selectedBar,
  width,
  height,
  max = 110,
  enableXLabels = true,
  xLabelEvery = 1,
  xLabelRotation = 0,
  xLabelFormatter,
}: OccupancyBarChartProps) => {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const bottomMargin = enableXLabels ? (xLabelRotation !== 0 ? 32 : 20) : 5;
    const margin = { top: 10, right: 0, bottom: bottomMargin, left: 0 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    let isOverMax = false;
    const sanitiseData = data.map((item) => {
      let value = item.value;
      if (value < 5) {
        value = 5; // For display purposes, no smaller than 5
      }
      if (value > max) {
        value = max;
        isOverMax = true;
      }
      return {
        ...item,
        value,
      };
    });

    const yMax = isOverMax ? max : Math.max(100, d3.max(sanitiseData, (d) => d.value) || 0);

    const x = d3
      .scaleBand<string>()
      .domain(sanitiseData.map((d) => d.label))
      .range([0, chartWidth])
      .padding(0.5);

    const y = d3.scaleLinear<number>().domain([0, yMax]).nice().range([chartHeight, 0]);

    svg.selectAll("*").remove();

    const chart = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    if (enableXLabels) {
      // Subset of labels actually shown on the axis (every Nth bar). The x-scale
      // still uses every bar's label for positioning — this only controls what
      // text we render.
      const shownLabels = new Set(
        sanitiseData
          .filter((_, i) => i % Math.max(1, xLabelEvery) === 0)
          .map((d) => d.label),
      );
      const labelByOriginal = new Map(sanitiseData.map((d, i) => [d.label, { original: d.label, index: i }]));

      const axisGroup = chart
        .append("g")
        .attr("transform", `translate(0,${chartHeight})`)
        .call(
          d3
            .axisBottom(x)
            .tickSize(0)
            .tickFormat((label) => {
              if (!shownLabels.has(label as string)) return "";
              const entry = labelByOriginal.get(label as string);
              if (!entry) return label as string;
              return xLabelFormatter ? xLabelFormatter(entry.original, entry.index) : entry.original;
            }),
        );

      const axisText = axisGroup
        .selectAll("text")
        .style("font-family", "Inter")
        .style("line-height", "16")
        .style("font-weight", "400")
        .style("font-size", "12px")
        .style("fill", "#595F6A");

      if (xLabelRotation !== 0) {
        axisText
          .style("text-anchor", xLabelRotation < 0 ? "end" : "start")
          .attr("transform", `translate(0,8) rotate(${xLabelRotation})`);
      } else {
        axisText.attr("transform", "translate(0,8)");
      }

      chart.select(".domain").remove();
    }

    chart.append("g").call(
      d3
        .axisLeft(y)
        .tickSize(0)
        .tickFormat(() => "")
    );
    chart.select(".domain").remove();

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", "#333")
      .style("color", "#fff")
      .style("padding", "5px")
      .style("border-radius", "4px")
      .style("font-size", "12px")
      .style("pointer-events", "none");

    const hasSmallDataSet = data.length <= SMALL_BAR_MAX_ITEM;
    const barWidth = hasSmallDataSet ? SMALL_BAR_WIDTH : Math.max(0, chartWidth / data.length - 5);

    // Add bars with animation
    const bars = chart
      .selectAll(".bar")
      .data(sanitiseData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.label)!)
      .attr("y", chartHeight) // Start bars at the bottom of the chart
      .attr("width", barWidth)
      .attr("height", 0) // Start with zero height
      .attr("fill", (d) =>
        getBarFillWithEmptyThreshold(
          percentToFraction(asPercent0100(d.value)),
          percentToFraction(BAR_EMPTY_PERCENT_THRESHOLD),
        ),
      )
      .attr("rx", 8)
      .attr("ry", 8)
      .on("mouseover", function (event, d) {
        if (d.tooltip) {
          tooltip.style("visibility", "visible").text(`${d.tooltip}`);
        }
      })
      .on("mousemove", function (event) {
        tooltip.style("top", event.pageY + 10 + "px").style("left", event.pageX + 10 + "px");
      })
      .on("mouseout", function () {
        tooltip.style("visibility", "hidden");
      });

    // Animate bars to their final position and height
    bars
      .transition()
      .duration(500)
      .ease(d3.easeCubicOut)
      .attr("y", (d) => y(d.value))
      .attr("height", (d) => chartHeight - y(d.value))
      .on("end", () => {
        if (selectedBar !== undefined) {
          chart.selectAll(".bar").each(function (_, i) {
            const bar = d3.select(this);
            if (i === selectedBar) {
              const barX = bar.attr("x");
              const barY = bar.attr("y");
              const barWidth = bar.attr("width");
              const barHeight = bar.attr("height");

              const addedSpace = hasSmallDataSet ? 8 : 6;

              let highlight: d3.Selection<SVGRectElement, unknown, null, undefined> = svg.select<
                SVGRectElement
              >("#selected-bar");
              if (highlight.empty()) {
                // Append only if it doesn't already exist
                highlight = svg.append<SVGRectElement>("rect").attr("id", "selected-bar");
              }

              highlight
                .attr("x", parseFloat(barX) - (hasSmallDataSet ? 4 : 3))
                .attr("y", parseFloat(barY) + (hasSmallDataSet ? 6 : 8))
                .attr("width", parseFloat(barWidth) + addedSpace)
                .attr("height", parseFloat(barHeight) + addedSpace)
                .attr("rx", 16)
                .attr("ry", 16)
                .attr("stroke", "#4045F8")
                .attr("stroke-width", 1.5)
                .attr("fill", "none");
            }
          });

          chart
            .selectAll("text")
            .filter((_, i) => i === selectedBar)
            .style("font-weight", "900")
            .style("font-size", "13px");
        }
      });
  }, [data, width, height, selectedBar, enableXLabels, max, xLabelEvery, xLabelRotation, xLabelFormatter]);

  return <svg ref={svgRef} width={width} height={height}></svg>;
};

export default OccupancyBarChart;
